import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutPanelTop,
  Users,
  Store,
  ChevronLeft,
  ChevronRight,
  CreditCard,
} from "lucide-react";
import { useDistributorStore } from "../../store/distributorStore";
import { usePartnerStore } from "../../store/partnerStore";
import { detectPartnerFromHost } from "../../partner/detect";
import { FeedbackModal } from "../overlays/FeedbackModal";

/**
 * Sidebar Component
 * ----------
 * The sidebar serves as the navigation menu for the application.
 * To add a new page, add a new item to the sidebarItems array, and ensure
 * that the route is added to the router (src/app/MerchantDashboard.tsx).
 **/

// Define names, routes, and icons for the sidebar
const sidebarItems = [
  {
    label: "Dashboard",
    icon: <LayoutPanelTop className="w-5 h-5" />,
    path: "/dashboard",
  },
  {
    label: "Team",
    icon: <Users className="w-5 h-5" />,
    path: "/team",
  },
  {
    label: "Billing",
    icon: <CreditCard className="w-5 h-5" />,
    path: "/billing",
  } 
];

const Sidebar: React.FC = () => {
  const location = useLocation();
  const { partnerName } = usePartnerStore();
  const { distributorName } = useDistributorStore();
  const { logoHref: logoSrc } = detectPartnerFromHost();
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleToggleCollapse = () => setIsCollapsed((prev) => !prev);

  // Sidebar item renderer
  const renderNavItem = (to: string, icon: React.ReactNode, name: string, collapsed: boolean) => {
    const isActive = location.pathname === to;

    return (
      <NavLink
        to={to}
        className="block rounded-lg relative"
        aria-label={name}
        title={name}
        tabIndex={0}
      >
        <div
          className={
            `flex items-center ${collapsed ? "justify-center gap-0 px-2" : "gap-3 px-2"} py-2 rounded-lg transition-colors text-[13px] duration-200 ` +
            (isActive
              ? "bg-white/10 text-white"
              : "hover:bg-white/5 text-white/80")
          }
        >
          <span
            className={`flex-shrink-0 ${
              isActive ? "text-white" : "text-white/60"
            }`}
          >
            {icon}
          </span>
          {!collapsed && (
            <span
              className={`${isActive ? "font-semibold text-white" : "text-white/70"}`}
            >
              {name}
            </span>
          )}
        </div>
      </NavLink>
    );
  };

  return (
    <aside className={`relative h-full ${isCollapsed ? "w-[72px]" : "w-[240px]"} overflow-hidden shadow-sm rounded-lg flex flex-col bg-[#0b0a12] border border-white/10 transition-all duration-300`}>
      {/* Header/Brand */}
      <div className="px-3 py-4 border-b border-white/10 bg-[#0b0a12]">
        <div className={`flex items-center ${isCollapsed ? "justify-center" : "justify-between"}`}>
          {!isCollapsed && (
            <div className="flex items-center">
              {logoSrc ? (
                <img
                  src={logoSrc}
                  alt={`${partnerName ?? "Partner"} logo`}
                  className="h-7 w-auto invert"
                />
              ) : (
                <span className="text-sm text-white/80">
                  {partnerName ?? "Dashboard"}
                </span>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={handleToggleCollapse}
            aria-expanded={!isCollapsed}
            className="inline-flex items-center justify-center rounded-md border border-white/10 bg-[#15112b]/60 p-2 text-white/80 hover:bg-[#15112b] focus:outline-none focus:ring-2 focus:ring-partnerPrimary"
            title={isCollapsed ? "Expand" : "Collapse"}
            tabIndex={0}
          >
            {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Distributor Mini Card */}
      {isCollapsed ? (
        <div
          className="flex items-center justify-center border-b border-white/10 bg-[#0b0a12] p-3"
          title={String(distributorName ?? "Distributor")}
          aria-label="Distributor"
        >
          <div className="h-9 w-9 rounded-md bg-partnerPrimary text-white flex items-center justify-center font-semibold ring-1 ring-white/10">
            <Store className="h-5 w-5 text-partnerPrimaryText" />
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 border-b border-white/10 bg-[#0b0a12] p-3">
          <div className="h-9 w-9 rounded-md bg-partnerPrimary text-white flex items-center justify-center font-semibold ring-1 ring-white/10">
            <Store className="h-5 w-5 text-partnerPrimaryText" />
          </div>
          <div className="min-w-0">
            <p className="text-[12px] text-white/70">Distributor</p>
            <p className="truncate text-[14px] font-medium text-white">
              {distributorName ?? "Distributor Name"}
            </p>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className={`flex-1 ${isCollapsed ? "px-2" : "px-3"} py-4 overflow-y-auto`} aria-label="Primary">
        <div className="space-y-1">
          {sidebarItems.map((item) => (
            <div key={item.path}>
              {renderNavItem(item.path, item.icon, item.label, isCollapsed)}
            </div>
          ))}
        </div>
      </nav>

      {/* Footer */}
      <div className="px-3 pb-4 pt-3 border-t border-white/10 space-y-3 bg-[#15112b]/40">
        {/* Quick Actions */}
        {/* <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setIsFeedbackModalOpen(true)}
            className="group flex items-center justify-center gap-2 rounded-md border border-white/10 bg-[#15112b] px-3 py-2 text-[12px] font-medium text-white/90 hover:bg-[#0b0a12]"
          >
            <MessageCircleHeart className="h-4 w-4 text-white/90" />
            Feedback
          </button>
          <NavLink
            to="/support"
            className="group flex items-center justify-center gap-2 rounded-md border border-white/10 bg-[#15112b] px-3 py-2 text-[12px] font-medium text-white/90 hover:bg-[#0b0a12]"
          >
            <LifeBuoy className="h-4 w-4" />
            Support
          </NavLink>
        </div> */}

        {!isCollapsed && (
          <div className="text-[10px] text-white/50 text-center">
            Powered by Valyrion
          </div>
        )}
      </div>

      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        onClose={() => setIsFeedbackModalOpen(false)}
        onSubmit={(data) => {
          console.log(data);
        }}
      />
    </aside>
  );
};

export default Sidebar;
